/**
 * =============================================================================
 * CLIENT-SIDE IMAGE PREPARATION (PR5a)
 * =============================================================================
 *
 * Best-effort downscale + re-encode of customer photos before upload
 * (ADR-022 §8 client half; BL-IMG-WIDGET-STAGING-COMPRESSION-001 compression
 * half). Zero-dependency: `createImageBitmap` + `<canvas>` only.
 *
 * ⚠️ NOT A SECURITY CONTROL. This module is a UX / bandwidth / server-cost
 * optimization ONLY. The Worker's upload chokepoint (magic-byte validation,
 * fail-closed EXIF strip + verify, 10 MB size cap, 40 MP header-first
 * dimension bound) remains the authoritative privacy and size gate for every
 * upload, whether or not this module ran. Nothing here may ever be relied on
 * server-side (ADR-022 §6.2: "never depend on the client").
 *
 * FAIL-OPEN BY DESIGN (ADR-022 §8: "Never blocks submission"): any failure —
 * unsupported input (HEIC/HEIF), decode error, low memory, missing browser
 * API, encoder fallback — resolves with the ORIGINAL file unchanged.
 *
 * Design decisions (PR5a, owner-approved 2026-07-03; backbone-review folds
 * annotated inline):
 *
 * F1 — OUTPUT FORMAT: preserve the input format.
 *   - JPEG → JPEG at q≈0.82 (the dominant phone-photo path; an upright,
 *     EXIF-free JPEG keeps the server on its zero-Cloudflare-Images-cost
 *     local-strip path per ADR-022 §14 D-ORIENT-IMPL).
 *   - WebP → WebP at q≈0.82, only when the browser truly encodes WebP
 *     (Safari's `toBlob('image/webp')` silently falls back to PNG, so the
 *     output blob's type is verified). Preserves alpha. Two container
 *     classes fail open untouched (backbone fold): LOSSLESS WebP (VP8L —
 *     screenshot-class content; lossy-transcoding it is the exact
 *     degradation the PNG rule prevents) and ANIMATED WebP (VP8X ANIM —
 *     `createImageBitmap` decodes only the first frame, and the server
 *     accepts animation today, so a re-encode would silently destroy it).
 *     VP8X-wrapped lossless is not detectable from the header and may still
 *     re-encode lossy (accepted residual).
 *   - PNG → PNG, and only as a 40 MP rescue. Never transcoded to a lossy
 *     format (ADR-022 §8: "never transcode PNG→JPEG"); PNGs are typically
 *     screenshots where downscaling costs text legibility, so a PNG the
 *     server would accept is uploaded untouched. The bound is read
 *     decode-free from the IHDR header (backbone fold — mirrors the
 *     server's header-first gate), so an in-bound PNG never pays a decode
 *     and an over-bound PNG is rescued at ANY byte size.
 *   - HEIC/HEIF → untouched (browsers cannot reliably decode them; the
 *     server transcodes HEIC→JPEG itself). In practice iOS Safari hands the
 *     file input a JPEG conversion of HEIF captures, which IS compressible
 *     here — that is how 48 MP+ "HEIF Max" shots get under the 40 MP bound.
 *   The output `File.type` always matches the encoded bytes: the server
 *   validates magic bytes AGAINST the claimed MIME type, so a mismatched
 *   type would be rejected with INVALID_FILE_SIGNATURE.
 *
 * F2 — TRIGGERS: lossy inputs (JPEG/WebP) at or below 1 MiB are skipped
 *   without decoding (re-encoding small files wastes CPU and can grow
 *   them). Larger inputs are downscaled to a 2048 px long edge (or
 *   re-encoded at original size when already within it — bandwidth +
 *   orientation bake). The result is kept only if it is SMALLER than the
 *   original — except when the original exceeds the server's 40 MP
 *   declared-dimension bound (IMAGE_DIMENSION_LIMITS.MAX_PIXELS), where the
 *   downscale is kept regardless because the original would be rejected
 *   outright (IMAGE_DIMENSIONS_EXCEEDED). A kept result must also respect
 *   the server's 10 MB byte cap (backbone fold: a dense >40 MP PNG rescue
 *   can re-encode past the cap; this module never CREATES an invalid file).
 *   Recorded residuals: files > 10 MB are rejected at select time by
 *   ImageUploader before this module ever runs; ≤ 1 MiB non-upright JPEGs
 *   stay unbaked (the server's CF-transform fallback path handles them, per
 *   D-ORIENT-IMPL); and a ≤ 1 MiB JPEG/WebP whose pixel count exceeds 40 MP
 *   (rare low-entropy scans) passes the pre-gate un-rescued and is
 *   server-rejected — decode-free dimension reads exist only for PNG, where
 *   they are format-guaranteed (backbone fold: recorded honestly).
 *
 * F3 — METADATA: a canvas re-encode inherently drops ALL metadata (EXIF,
 *   GPS, ICC, XMP) client-side — a privacy improvement (GPS never leaves
 *   the device for compressed files) and the reason re-encoded JPEGs land
 *   on the server's zero-cost Orientation∈{1,absent} path. The server's
 *   fail-closed strip + write-time verify remains the guarantee.
 *
 * ORIENTATION SAFETY GATE: if a browser decoded WITHOUT applying EXIF
 * orientation and we re-encoded, the Orientation tag would be gone and the
 * server could never right the image — a permanently sideways photo. That
 * cannot be feature-detected from the API surface (unknown WebIDL dictionary
 * members are silently ignored), so compression is gated on a one-time probe:
 * decode a tiny embedded Orientation=6 JPEG and verify the dimensions swap.
 * Browsers that fail the probe (or lack `createImageBitmap`) upload
 * originals. A probe decode that THROWS is treated as transient and retried
 * on a later call (backbone fold: a momentary failure must not disable
 * compression for the whole session); only completed determinations are
 * cached. The fixture was generated by Chromium's own encoder (2×1 gray
 * JPEG, JFIF + ICC APP segments stripped, Orientation=6 EXIF APP1 spliced
 * in) and its oriented decode (1×2) was verified against real Chromium —
 * other engines were not testable in the build environment; an engine that
 * cannot decode the fixture fails open (no compression, never a corrupt
 * upload).
 *
 * No logging on the fail-open path by design: fallbacks are expected
 * behavior, not errors, and the widget logger is console-based (stripped by
 * terser `drop_console` in production builds anyway). Keeping this module
 * silent also protects the gzip bundle budget (BL-WIDGET-BUNDLE-BUDGET-001).
 *
 * ARCHITECTURE REFERENCE: docs/ARCHITECTURE.md §11.4 (client-side resize)
 * DECISION RECORD: docs/CHANGELOG.md PR5a entry; ADR-022 §7/§8/§12 PR 5
 *
 * =============================================================================
 */

import { FILE_VALIDATION, IMAGE_DIMENSION_LIMITS } from '@find-my-part/types';

// =============================================================================
// TUNING CONSTANTS (client-only knobs; the shared client/server contract —
// MIME allow-list, byte cap, pixel bound — lives in @find-my-part/types.
// NOTE: if ADR-022 §14 D-DIM's deferred server-side "long-edge ≤ 2048 stored"
// downscale ever lands, TARGET_LONG_EDGE becomes a shared contract value and
// must move to @find-my-part/types beside IMAGE_DIMENSION_LIMITS.)
// =============================================================================

/** Target long edge after downscale (ADR-022 §8 / BACKLOG PR5 row: ~2048 px). */
export const COMPRESSION_TARGET_LONG_EDGE_PX = 2048;

/** Lossy re-encode quality (ADR-022 §8 / BACKLOG PR5 row: q ≈ 0.82). */
export const COMPRESSION_QUALITY = 0.82;

/** Lossy files at or below this size are never touched (1 MiB pre-gate, F2). */
export const COMPRESSION_MIN_SOURCE_BYTES = 1024 * 1024;

/**
 * Defensive bound on the canvas encode (external round 1, Codex): the spec
 * guarantees `toBlob` settles, but a stalled encoder in a buggy WebView must
 * fail open rather than hang the submit loop forever. Generous — a real
 * ≤2048px encode completes in well under a second even on slow devices.
 * `createImageBitmap` is deliberately NOT bounded: it is equally
 * spec-settling, and a decode timeout would abort genuinely slow 48 MP
 * rescue decodes on low-end phones — turning a slow success into a server
 * reject, the opposite of this module's purpose.
 */
export const COMPRESSION_ENCODE_TIMEOUT_MS = 10_000;

/**
 * 2×1 gray JPEG carrying an EXIF Orientation=6 tag. A browser that honors
 * EXIF orientation decodes it as 1×2 (see ORIENTATION SAFETY GATE above).
 */
const ORIENTATION_PROBE_JPEG_B64 =
  '/9j/4QAiRXhpZgAASUkqAAgAAAABABIBAwABAAAABgAAAAAAAAD/2wBDABALDA4MChAODQ4SERAT' +
  'GCgaGBYWGDEjJR0oOjM9PDkzODdASFxOQERXRTc4UG1RV19iZ2hnPk1xeXBkeFxlZ2P/2wBDARES' +
  'EhgVGC8aGi9jQjhCY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2Nj' +
  'Y2NjY2P/wAARCAABAAIDASIAAhEBAxEB/8QAFAABAAAAAAAAAAAAAAAAAAAAAP/EABQQAQAAAAAA' +
  'AAAAAAAAAAAAAAD/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/a' +
  'AAwDAQACEQMRAD8AAA//2Q==';

// =============================================================================
// ORIENTATION PROBE
// =============================================================================

let orientationProbe: Promise<boolean> | undefined;

/**
 * Decode the embedded Orientation=6 fixture and report whether the browser
 * applied EXIF orientation (dimensions swap to 1×2). A missing
 * `createImageBitmap` is a stable determination; a decode THROW propagates
 * to the caller, which treats it as transient.
 */
async function probeOrientation(): Promise<boolean> {
  if (typeof createImageBitmap !== 'function') return false;
  const binary = atob(ORIENTATION_PROBE_JPEG_B64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const bitmap = await createImageBitmap(new Blob([bytes], { type: 'image/jpeg' }), {
    imageOrientation: 'from-image',
  });
  const applied = bitmap.width === 1 && bitmap.height === 2;
  bitmap.close?.();
  return applied;
}

/**
 * One-time (cached) probe: does this browser apply EXIF orientation when
 * decoding via `createImageBitmap(..., { imageOrientation: 'from-image' })`?
 * Completed determinations are cached for the session; a thrown decode is
 * transient — it answers `false` for the current round but clears the cache
 * so a later call retries (bounded: one ~300-byte decode attempt per call).
 */
function browserAppliesExifOrientation(): Promise<boolean> {
  if (!orientationProbe) {
    const probe: Promise<boolean> = probeOrientation().catch(() => {
      // Identity guard: only clear the cache entry this probe owns.
      if (orientationProbe === probe) orientationProbe = undefined;
      return false;
    });
    orientationProbe = probe;
  }
  return orientationProbe;
}

/**
 * Clears the cached orientation-probe result so each test controls it.
 *
 * @internal Test-only. Never call from production code.
 */
export function __resetOrientationProbeForTests(): void {
  orientationProbe = undefined;
}

// =============================================================================
// ENCODING
// =============================================================================

/**
 * Draw the bitmap onto a canvas at the target size and encode it.
 * Resolves `null` when the environment has no usable 2D canvas (and, per
 * spec, when a target dimension is 0 — degenerate aspect ratios fail open).
 */
async function encodeBitmap(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  type: string
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  try {
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    // Quality is ignored for image/png (lossless) per the canvas spec.
    // Raced against COMPRESSION_ENCODE_TIMEOUT_MS (see the constant's
    // docblock); a late callback after the timeout resolves is a no-op.
    return await new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), COMPRESSION_ENCODE_TIMEOUT_MS);
      canvas.toBlob(
        (blob) => {
          clearTimeout(timer);
          resolve(blob);
        },
        type,
        COMPRESSION_QUALITY
      );
    });
  } finally {
    // Release the backing store promptly — iOS Safari pools canvas memory
    // and reclaims lazily; a 5-image submit would otherwise strand ~60 MB.
    canvas.width = 0;
    canvas.height = 0;
  }
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Best-effort client-side downscale/re-encode of an image before upload.
 *
 * Never throws and never rejects: any failure resolves with the ORIGINAL
 * file (fail-open — see the module header). The returned file keeps the
 * original name (the server treats filenames as opaque and never serves
 * them), MIME type (F1: preserve format), and lastModified.
 *
 * @param file - The user-selected image file
 * @returns The compressed file when compression applies and helps, otherwise
 *          the original file unchanged
 *
 * @example
 * const uploadFile = await prepareImageForUpload(selected);
 * await widgetApi.images.upload(questionId, uploadFile);
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  try {
    const type = file.type;
    const lossy = type === 'image/jpeg' || type === 'image/webp';
    // HEIC/HEIF and anything unrecognized: not reliably decodable (F1).
    if (!lossy && type !== 'image/png') return file;

    if (type === 'image/png') {
      // F1 PNG policy, decode-free: width/height sit at fixed offsets in the
      // IHDR chunk (signature 0-7, length 8-11, 'IHDR' 12-15, width 16-19,
      // height 20-23 — the same header-first read the server's bound uses).
      // An in-bound PNG uploads untouched at ANY size; only a PNG the server
      // would reject (>40 MP) proceeds to the downscale rescue — hence PNG
      // deliberately bypasses the byte pre-gate below (a ≤1 MiB >40 MP
      // flat-color screenshot is still rescued).
      const head = new DataView(await file.slice(0, 24).arrayBuffer());
      if (head.getUint32(12) !== 0x49484452) return file; // no IHDR — server's gate decides
      const declaredPixels = head.getUint32(16) * head.getUint32(20);
      if (declaredPixels <= IMAGE_DIMENSION_LIMITS.MAX_PIXELS) return file;
    } else if (file.size <= COMPRESSION_MIN_SOURCE_BYTES) {
      // F2 pre-gate: small lossy files are never worth a decode + re-encode.
      return file;
    } else if (type === 'image/webp') {
      // F1 WebP container sniff (offsets mirror the server's
      // readWebpDimensions): LOSSLESS WebP is never lossy-transcoded, and an
      // ANIMATED WebP would be silently flattened to its first frame by
      // createImageBitmap while the server accepts animation — both upload
      // untouched. Lossless hides in two shapes: a bare VP8L first chunk
      // (cwebp -lossless), or a VP8L bitstream chunk inside a VP8X extended
      // container (Chromium's own canvas encoder emits VP8X for everything,
      // verified against real Chromium) — so for VP8X, after checking the
      // Animation flag (payload byte 20, bit 0x02), walk the chunk headers
      // within a bounded window to find the bitstream chunk. Undetermined
      // (bitstream beyond the window / malformed sizes / short head — reads
      // as zeros) stays untouched: never degrade what we cannot classify.
      const head = new Uint8Array(await file.slice(0, 16384).arrayBuffer());
      const view = new DataView(head.buffer);
      const fourccAt = (o: number): string =>
        String.fromCharCode(head[o], head[o + 1], head[o + 2], head[o + 3]);
      const first = fourccAt(12);
      if (first === 'VP8L') return file;
      if (first === 'VP8X') {
        if ((head[20] & 0x02) !== 0) return file; // animated
        let bitstream = '';
        let offset = 20 + view.getUint32(16, true) + (view.getUint32(16, true) & 1);
        while (offset + 8 <= head.length) {
          const chunk = fourccAt(offset);
          if (chunk === 'VP8 ' || chunk === 'VP8L') {
            bitstream = chunk;
            break;
          }
          const size = view.getUint32(offset + 4, true);
          offset += 8 + size + (size & 1); // chunks are padded to even sizes
        }
        if (bitstream !== 'VP8 ') return file; // lossless or undetermined
      }
    }

    // Orientation safety gate: without a provably orientation-correct
    // decode, re-encoding could store a permanently sideways image.
    if (!(await browserAppliesExifOrientation())) return file;

    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    try {
      const { width, height } = bitmap;
      const exceedsServerBound = width * height > IMAGE_DIMENSION_LIMITS.MAX_PIXELS;

      // Safety net for decoded-vs-declared divergence: a PNG only reaches
      // here when its declared dimensions exceed the bound (see above).
      if (!lossy && !exceedsServerBound) return file;

      const longEdge = Math.max(width, height);
      const scale =
        longEdge > COMPRESSION_TARGET_LONG_EDGE_PX
          ? COMPRESSION_TARGET_LONG_EDGE_PX / longEdge
          : 1;
      const blob = await encodeBitmap(
        bitmap,
        Math.round(width * scale),
        Math.round(height * scale),
        type
      );

      // Encoder unavailable/stalled, silently fell back to another format
      // (e.g. Safari returns PNG when asked for WebP — the server validates
      // magic bytes against the claimed MIME type, so only a same-type
      // result works), or emitted zero bytes on an encoder/resource failure
      // (the server rejects empty uploads with EMPTY_FILE) — fail open.
      if (!blob || blob.type !== type || blob.size === 0) return file;

      // Never CREATE a file that violates the server's 10 MB byte cap: the
      // rescue path bypasses the smaller-only guard, and a dense >40 MP PNG
      // can re-encode past the cap. The original at least fails with the
      // error that truthfully describes the file the user selected.
      if (blob.size > FILE_VALIDATION.MAX_SIZE_BYTES) return file;

      // F2: keep the re-encode only when it helps — smaller bytes, or it
      // rescues an upload the server's 40 MP dimension bound would reject.
      if (blob.size >= file.size && !exceedsServerBound) return file;

      return new File([blob], file.name, { type, lastModified: file.lastModified });
    } finally {
      bitmap.close?.();
    }
  } catch {
    // Fail open — never blocks submission (ADR-022 §8).
    return file;
  }
}
